import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { LyricsReviewPanel } from "@/components/lyrics/LyricsReviewPanel";
import { safeApiError } from "@/lib/safeErrorText";
import { runLyricsJob } from "@/lib/lyricsJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMEOUT_MS = 150_000;

/** True when the text already looks like lyrics (section headers or several lines) rather than an idea. */
export function looksLikeLyrics(text = "") {
  const t = text.trim();
  return /\[[^\]]+\]/.test(t) || t.split(/\r?\n/).filter((l) => l.trim()).length >= 4;
}

/** Creator-mode lyric assist: ChatGPT writes from the idea, or polishes the writer's own lines while preserving them. */
export function LyricsAssist({ title, styleLabel, lyrics, onLyrics, disabled, onBusyChange }) {
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null); // writing | reviewing
  const [review, setReview] = useState(null);
  const [notes, setNotes] = useState([]);
  const [runMode, setRunMode] = useState(null); // "polish" | "write" — frozen while busy (the draft fills the textarea mid-run)
  const hasOwnLyrics = busy && runMode ? runMode === "polish" : looksLikeLyrics(lyrics);
  const canRun = !busy && !disabled && (lyrics.trim().length > 0 || title.trim().length > 0);

  const run = async () => {
    if (!canRun) return;
    setBusy(true);
    onBusyChange?.(true);
    setRunMode(hasOwnLyrics ? "polish" : "write");
    try {
      if (hasOwnLyrics) {
        const { data } = await axios.post(`${API}/lyrics/refine`, {
          lyrics, action: "polish", genre: styleLabel, title, preserve_original: true,
        }, { timeout: TIMEOUT_MS });
        onLyrics(data.lyrics);
        setReview(null);
        setNotes([data.note].filter(Boolean));
        toast.success("Polished — your writing preserved");
      } else {
        setStage("writing");
        const job = await runLyricsJob({
          prompt: lyrics.trim() || `A ${styleLabel} song called "${title}"`,
          genre: styleLabel, title, review: true,
          structure: "Verse-Chorus-Verse-Chorus-Bridge-Chorus",
        }, {
          onUpdate: (j) => {
            if (j.status === "reviewing" && j.draft) { onLyrics(j.draft); setStage("reviewing"); }
          },
        });
        onLyrics(job.lyrics);
        setReview(job.review || null);
        setNotes([]);
        toast.success("Lyrics written & reviewed", { description: job.model_label || "ChatGPT" });
      }
    } catch (e) {
      toast.error(hasOwnLyrics ? "Could not polish lyrics" : "Could not write lyrics", { description: safeApiError(e, e?.message) });
    } finally {
      setBusy(false);
      setStage(null);
      setRunMode(null);
      onBusyChange?.(false);
    }
  };

  return (
    <div data-testid="lyrics-assist" className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="lyrics-assist-btn"
          onClick={run}
          disabled={!canRun}
          className="inline-flex items-center gap-2 rounded-xl border border-fuchsia-400/35 bg-fuchsia-500/15 px-3.5 py-2 text-sm font-medium text-fuchsia-50 transition-colors hover:bg-fuchsia-500/25 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : hasOwnLyrics ? <ShieldCheck className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          {busy ? (hasOwnLyrics ? "Polishing…" : stage === "reviewing" ? "Draft ready — reviewing…" : "Writing…") : hasOwnLyrics ? "Polish my lyrics (keep my words)" : "Write lyrics with ChatGPT"}
        </button>
        <span data-testid="lyrics-assist-hint" className="text-xs text-white/55">
          {hasOwnLyrics
            ? "Fixes forced rhymes, grammar and flow while keeping your voice, images and key lines."
            : "ChatGPT writes from your idea, reviews for weak lines and forced rhymes, then improves them."}
        </span>
      </div>
      <div className="mt-3">
        <LyricsReviewPanel review={review} notes={notes} onDismiss={() => { setReview(null); setNotes([]); }} testId="creator-lyrics-review" />
      </div>
    </div>
  );
}

export default LyricsAssist;
